import { AlarmIcon, CameraIcon, DoorIcon } from '@hivewatch/dark'

import { AssociatedDevicesQuery } from '@/graphql/generated/operations'
import { DeviceType } from '@/graphql/generated/schemas'

import {
  AssociatedDeviceRow,
  AssociationStatus,
  GridItem,
} from '../types/types'

export const colorTokens = {
  dark: {
    bgColor: 'bg.app',
    inputBgColor: 'bg.input',
    hoverBgColor: undefined,
    textColor: 'icon.secondary',
  },
  light: {
    bgColor: 'white',
    inputBgColor: 'light.bg.modal',
    hoverBgColor: 'light.interactive.tertiary-border' as string | undefined,
    textColor: 'light.icon.secondary',
  },
}

const ICON_COLORS = {
  light: 'var(--hw-dark-colors-light-icon-secondary, #94A3B8)',
  dark: 'var(--hw-dark-colors-icon-secondary, #94A3B8)',
}

export const getDeviceIcon = (
  deviceType: DeviceType,
  mode: 'light' | 'dark'
) => {
  const color = ICON_COLORS[mode]
  switch (deviceType) {
    case DeviceType.Alarm:
      return <AlarmIcon color={color} size={20} />
    case DeviceType.Door:
      return <DoorIcon color={color} size={20} />
    case DeviceType.Camera:
      return <CameraIcon color={color} size={20} />
    default:
      return <CameraIcon color={color} size={20} />
  }
}

const getStatus = (
  grids: (GridItem | null)[] | null | undefined
): AssociationStatus => {
  if (!grids?.length) return 'associated'
  if (grids.some((g) => g?.isTailgateEnabled === true))
    return 'tailgate-enabled'
  if (grids.some((g) => g?.inBox != null)) return 'grid-enabled'
  return 'associated'
}

export const deriveRows = (
  data: AssociatedDevicesQuery | undefined,
  deviceId: string
): AssociatedDeviceRow[] => {
  const device = data?.device
  if (!device) return []

  const conns = device.tailgateConns?.edges ?? []
  const isCamera = device.type === DeviceType.Camera

  if (isCamera) {
    return conns.flatMap((edge) => {
      const conn = edge?.node
      const door = conn?.door
      if (!door?.id) return []

      const currentCameraNode = conn?.cameras?.edges?.find(
        (e) => e?.node?.id === deviceId
      )?.node
      const grids = currentCameraNode?.camera?.tailgateGrids?.filter(
        (g) => g?.door?.id === door.id
      )

      return [
        {
          id: door.id,
          name: door.name ?? door.id,
          status: getStatus(grids),
          deviceType: door.type ?? DeviceType.Door,
          connectionId: conn.id,
        },
      ]
    })
  }

  // Door / AC device — show associated cameras
  return conns.flatMap((edge) => {
    const conn = edge?.node
    return (conn?.cameras?.edges ?? []).flatMap((cameraEdge) => {
      const cam = cameraEdge?.node
      if (!cam?.id) return []

      const grids = cam.camera?.tailgateGrids?.filter(
        (g) => g?.door?.id === deviceId
      )

      return [
        {
          id: cam.id,
          name: cam.name ?? cam.id,
          status: getStatus(grids),
          deviceType: cam.type ?? DeviceType.Camera,
          connectionId: conn.id,
        },
      ]
    })
  })
}
